/**
 * Firebase Auth client — initializes the Firebase Web SDK by fetching
 * the PUBLIC config from `/api/firebase-config` on boot, then exposes
 * `signIn`, `signOut`, `getCurrentUser`, `getIdToken`, and subscription
 * helpers for the React tree.
 *
 * Design intent:
 *   - Backend ships the web config (apiKey / authDomain / projectId /
 *     etc.) so the kit can swap projects per tenant deployment without
 *     a frontend rebuild.
 *   - The fetch wrapper at `src/lib/api.ts` reads `getIdToken()` and
 *     attaches `Authorization: Bearer <jwt>` to every API call.
 *   - Single-source-of-truth for sign-in state: subscribers register
 *     via `subscribe(listener)` and React state hooks call it from a
 *     useEffect.
 */

import {
  type Auth,
  type User,
  createUserWithEmailAndPassword,
  getAuth,
  onAuthStateChanged,
  onIdTokenChanged,
  signInWithEmailAndPassword,
  signOut as fbSignOut,
} from "firebase/auth";
import { initializeApp, type FirebaseApp } from "firebase/app";

type WebConfig = {
  apiKey?: string;
  authDomain?: string;
  projectId?: string;
  storageBucket?: string;
  messagingSenderId?: string;
  appId?: string;
};

type Listener = (user: User | null) => void;

let _app: FirebaseApp | null = null;
let _auth: Auth | null = null;
let _configError: string | null = null;
let _bootPromise: Promise<void> | null = null;
const _listeners: Set<Listener> = new Set();
let _lastUser: User | null = null;
// Synchronously-readable cache of the current ID token. Kept fresh by
// onIdTokenChanged (fires on sign-in, sign-out, and hourly refresh).
// Used to build `?token=` download/image URLs, since browser navigations
// (<a href>, <img src>, window.open) can't carry an Authorization header.
let _cachedToken: string | null = null;
// Resolves once a `__session` cookie has been (re)established for the
// current signed-in user. AuthGate awaits this before rendering children
// that iframe /career, so the first iframe load carries the cookie instead
// of 401ing until a reload. Reset on each onIdTokenChanged so a fresh token
// always re-mints the cookie.
let _sessionReady: Promise<void> = Promise.resolve();
let _resolveSessionReady: (() => void) | null = null;

/**
 * Exchange the current Firebase ID token for a `__session` cookie via
 * POST /api/auth/sessionLogin. The cookie gates transports that can't
 * carry an Authorization header (the careers-page iframe + its relative
 * fetches). Resilient: a failure never blocks sign-in — we log and move on.
 */
async function _establishSessionCookie(token: string): Promise<void> {
  try {
    await fetch("/api/auth/sessionLogin", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ idToken: token }),
    });
  } catch (err) {
    // Non-fatal: API-token auth still works for /api/*; only the iframe
    // /career path needs the cookie. Log so it's diagnosable.
    console.warn("sessionLogin failed (career iframe may 401):", err);
  }
}

/** Promise that resolves once the `__session` cookie is established for the
 *  signed-in user (or immediately when signed-out). Await before rendering
 *  anything that iframes /career. */
export function sessionCookieReady(): Promise<void> {
  return _sessionReady;
}

/**
 * Initialize Firebase. Fetches `/api/firebase-config`, validates the
 * returned config has the minimum fields, and constructs the app.
 * Idempotent — subsequent calls return the same promise.
 */
// Build-time fallback config (Vite reads VITE_* env from .env.production etc.
// at build time and embeds them as constants). Used when /api/firebase-config
// is unreachable — e.g. Firebase-Hosting-only deploys where there's no backend
// yet. Kit buyers can either set these env vars before `npm run build` OR
// rely on the runtime endpoint once their backend is reachable.
const _buildConfig: WebConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

function _hasMinimumConfig(cfg: WebConfig): boolean {
  return !!(cfg.apiKey && cfg.authDomain && cfg.projectId);
}

export function bootFirebase(): Promise<void> {
  if (_bootPromise) return _bootPromise;
  _bootPromise = (async () => {
    let cfg: WebConfig = {};
    // Try backend endpoint first (works when API gateway is wired up).
    try {
      const res = await fetch("/api/firebase-config");
      if (res.ok) {
        cfg = await res.json();
      }
    } catch (_) {
      // network failure (offline, no backend reachable) — fall through to build-time fallback
    }
    // Fall back to build-time embedded config if endpoint didn't return usable data.
    if (!_hasMinimumConfig(cfg)) {
      if (_hasMinimumConfig(_buildConfig)) {
        cfg = _buildConfig;
      } else {
        _configError =
          "Firebase config incomplete — set FIREBASE_WEB_API_KEY, " +
          "FIREBASE_WEB_AUTH_DOMAIN, and FIREBASE_PROJECT_ID on the backend, " +
          "OR set the VITE_FIREBASE_* equivalents at build time.";
        return;
      }
    }
    try {
      _app = initializeApp(cfg);
      _auth = getAuth(_app);
      onAuthStateChanged(_auth, (user) => {
        _lastUser = user;
        for (const l of _listeners) l(user);
      });
      // Keep the synchronous token cache fresh. onIdTokenChanged fires on
      // login, logout, and silent hourly refresh — exactly when the token
      // we embed in download URLs would otherwise go stale.
      onIdTokenChanged(_auth, async (user) => {
        if (!user) {
          _cachedToken = null;
          _sessionReady = Promise.resolve();
          return;
        }
        // Arm the readiness gate before we have a token so awaiters block
        // until the cookie POST resolves.
        _sessionReady = new Promise<void>((res) => { _resolveSessionReady = res; });
        try { _cachedToken = await user.getIdToken(); }
        catch { _cachedToken = null; }
        // Establish / refresh the `__session` cookie so the careers iframe
        // (relative fetches, no Authorization header) is authorized.
        if (_cachedToken) await _establishSessionCookie(_cachedToken);
        _resolveSessionReady?.();
        _resolveSessionReady = null;
      });
    } catch (err: any) {
      _configError = `Firebase init failed: ${err?.message || String(err)}`;
    }
  })();
  return _bootPromise;
}

/** Subscribe to auth state changes. Returns an unsubscribe fn. */
export function subscribe(listener: Listener): () => void {
  _listeners.add(listener);
  // Fire immediately with current state so the subscriber doesn't have
  // to special-case "first run".
  listener(_lastUser);
  return () => {
    _listeners.delete(listener);
  };
}

export function getCurrentUser(): User | null {
  return _lastUser;
}

export function getConfigError(): string | null {
  return _configError;
}

/** Get a fresh ID token for the signed-in user. Null when signed-out. */
export async function getIdToken(): Promise<string | null> {
  const user = _lastUser;
  if (!user) return null;
  try {
    return await user.getIdToken(/* forceRefresh */ false);
  } catch {
    return null;
  }
}

/** Synchronously read the cached ID token (null when signed-out or not
 *  yet fetched). For building `?token=` URLs on plain browser navigations
 *  that can't set an Authorization header — file downloads + <img src>. */
export function getCachedIdToken(): string | null {
  return _cachedToken;
}

/** Append the cached Firebase ID token as a `?token=` query param so a
 *  plain browser navigation (download link, image src, new tab) carries
 *  auth over the public Funnel. No-op on LAN/Tailnet (no token cached) —
 *  there the backend's trusted-local bypass already authorizes the GET. */
export function appendAuthToken(url: string): string {
  if (!_cachedToken) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}token=${encodeURIComponent(_cachedToken)}`;
}

export async function signIn(email: string, password: string): Promise<void> {
  if (!_auth) {
    throw new Error(_configError || "Firebase not initialized yet");
  }
  await signInWithEmailAndPassword(_auth, email, password);
}

/** Self-signup. Creates a Firebase Auth account and signs the user in.
 *  After signup the user has a Firebase UID but isn't yet mapped to a tenant
 *  in state/tenants.json — backend protected routes will 403 until an
 *  operator adds the entry. Frontend shows "no tenant assigned" state. */
export async function signUp(email: string, password: string): Promise<void> {
  if (!_auth) {
    throw new Error(_configError || "Firebase not initialized yet");
  }
  await createUserWithEmailAndPassword(_auth, email, password);
}

export async function signOut(): Promise<void> {
  if (!_auth) return;
  // Clear the server-side `__session` cookie too. Resilient: a failed POST
  // never blocks the Firebase sign-out.
  try {
    await fetch("/api/auth/sessionLogout", { method: "POST" });
  } catch (err) {
    console.warn("sessionLogout failed:", err);
  }
  await fbSignOut(_auth);
}
